import type { TFunction } from 'i18next';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled, { DefaultTheme, useTheme } from 'styled-components';

import { ExtendedProjectStatus } from '../../../../../typings/api/skymap/rest/v0/.common';
import { GetProjectsQueryParameters } from '../../../../../typings/api/skymap/rest/v1/project';
import { formattedDate } from '../../../utils/dateUtils';
import { toHumanReadableFileSize } from '../../../utils/io/file';
import { sessionStorageUtils } from '../../../utils/session-storage-utils';
import { isDefined } from '../../../utils/variables';
import { useExpiredLicenseGuard } from '../../hooks/use-expired-license-guard';
import { CreateProjectButton } from '../../routes/projects/create-project-button';
import { useAppContext } from '../../state/app-state';
import {
  DataTable,
  DataTableColumns,
  DataTableSortOrder,
  DataTableSortType,
  ServerSortingProps,
} from '../data-table/data-table';
import { getNumberOfPages } from '../data-table/pagination';
import { Icon } from '../icon/icon';
import { Stack } from '../stack/stack';
import {
  ActiveFilter,
  FilterDefinition,
  TableFilterButton,
  TableFilterChips,
} from '../table-filter-bar/table-filter-bar';
import { TextBox } from '../text-box/text-box';
import { useProjectSearchFilter } from './project-search-filter';
import { resolveExtendedStatus } from './resolve-extended-status';
import { StatusFilter } from './status-filter-box';
import { useGetCompaniesQuery } from './use-get-companies-query';
import { useGetV1CompaniesQuery } from './use-get-v1-companies-query';
import { useProjectsQuery } from './use-projects-query';

const pageSize = 15;

interface ProjectTableView extends Record<string, any> {
  status: React.ReactElement;
  id: string;
  name: string;
  company: string;
  companyId: string | undefined;
  created?: string;
  size: number;
}

const ProjectStatusIcon = (status: ExtendedProjectStatus, theme: DefaultTheme, t: TFunction) => {
  switch (status) {
    case 'ACTIVE':
      return (
        <Icon
          color={theme.color.green}
          icon={['fas', 'circle']}
          title={t('unifiedProjectList.statusIcon.active', { ns: 'components' })}
        />
      );
    case 'ARCHIVED':
      return (
        <Icon
          color={'#000'}
          icon={['fas', 'circle']}
          title={t('unifiedProjectList.statusIcon.archived', { ns: 'components' })}
        />
      );
    case 'ARCHIVE_ERROR':
      return (
        <Icon
          color={theme.color.red}
          icon={['fas', 'triangle-exclamation']}
          title={t('unifiedProjectList.statusIcon.archiveError', { ns: 'components' })}
        />
      );
    case 'ARCHIVE_IDLE':
      return (
        <Icon
          color={theme.color.brand.dark}
          icon={['fas', 'circle']}
          title={t('unifiedProjectList.statusIcon.archiveIdle', { ns: 'components' })}
        />
      );
    case 'ARCHIVE_IN_PROGRESS':
      return (
        <Icon
          color="#000"
          icon={['fas', 'spinner']}
          spin={true}
          title={t('unifiedProjectList.statusIcon.archiveInProgress', { ns: 'components' })}
        />
      );
    case 'DISABLED':
      return (
        <Icon
          color={theme.color.red}
          icon={['fas', 'circle']}
          title={t('unifiedProjectList.statusIcon.disabled', { ns: 'components' })}
        />
      );
    case 'RESTORE_DONE':
      return (
        <Icon
          color={theme.color.green}
          icon={['fas', 'circle']}
          title={t('unifiedProjectList.statusIcon.restoreDone', { ns: 'components' })}
        />
      );
    case 'RESTORE_ERROR':
      return (
        <Icon
          color={theme.color.red}
          icon={['fas', 'exclamation-triangle']}
          title={t('unifiedProjectList.statusIcon.restoreError', { ns: 'components' })}
        />
      );
    case 'RESTORE_IDLE':
      return (
        <Icon
          color={theme.color.brand.dark}
          icon={['fas', 'circle']}
          title={t('unifiedProjectList.statusIcon.restoreIdle', { ns: 'components' })}
        />
      );
    case 'RESTORE_IN_PROGRESS':
      return (
        <Icon
          color="#000"
          icon={['fas', 'spinner']}
          spin={true}
          title={t('unifiedProjectList.statusIcon.restoreInProgress', { ns: 'components' })}
        />
      );
  }
};

const tableStyle = {
  rowCell: {
    fontSize: '14px',
  },
  headerCell: {
    fontSize: '14px',
  },
};

type OrderBy = GetProjectsQueryParameters['orderBy'];

function toOrderBy(columnKey: string, order: DataTableSortOrder): OrderBy {
  if (columnKey === 'name') {
    return order === DataTableSortOrder.ASC ? 'name:asc' : 'name:desc';
  }
  if (columnKey === 'created') {
    return order === DataTableSortOrder.ASC ? 'createdAt:asc' : 'createdAt:desc';
  }
  return undefined;
}

type LifecycleStatus = NonNullable<GetProjectsQueryParameters['lifecycleStatus']>[number];

function toLifecycleStatusFilter(filter: StatusFilter): LifecycleStatus[] | undefined {
  if (filter === 'ALL') {
    return undefined;
  }
  if (filter === 'ALL_ARCHIVED') {
    return ['ARCHIVED', 'ARCHIVE_IDLE', 'ARCHIVE_IN_PROGRESS', 'ARCHIVE_ERROR'];
  }
  if (filter === 'ALL_RESTORED') {
    return ['RESTORE_DONE', 'RESTORE_IDLE', 'RESTORE_IN_PROGRESS', 'RESTORE_ERROR'];
  }
  return [filter];
}

function parseOrderBy(
  orderBy: OrderBy,
): { columnKey: string; order: DataTableSortOrder } | undefined {
  if (!orderBy) {
    return undefined;
  }
  const [field, dir] = orderBy.split(':');
  const columnKey = field === 'createdAt' ? 'created' : field;
  const order = dir === 'asc' ? DataTableSortOrder.ASC : DataTableSortOrder.DESC;
  return { columnKey, order };
}

const UnifiedProjectList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { isOrganizationAdmin, hasAdminRights, currentUser } = useAppContext();

  const [currentPage, setCurrentPage] = React.useState(1);
  const { searchText, setSearchText } = useProjectSearchFilter();
  const [debouncedSearchText, setDebouncedSearchText] = React.useState(searchText);
  const [orderBy, setOrderBy] = React.useState<OrderBy>('createdAt:desc');

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchText(searchText), 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Active filters — persisted in session storage for backward compat with old status filter key.
  const savedStatus = sessionStorageUtils.getUserItemOrDefault('projectListStatusFilter', 'ACTIVE');
  const savedCompany = sessionStorageUtils.getUserItemOrDefault('projectListCompanyFilter', '');
  const [activeFilters, setActiveFilters] = React.useState<ActiveFilter[]>([
    ...(savedStatus !== 'ALL' ? [{ key: 'status', value: savedStatus }] : []),
    ...(savedCompany ? [{ key: 'company', value: savedCompany }] : []),
  ]);
  const handleFiltersChange = useCallback((next: ActiveFilter[]) => {
    const statusValue = next.find((f) => f.key === 'status')?.value ?? 'ALL';
    sessionStorageUtils.setUserItem('projectListStatusFilter', statusValue as StatusFilter);
    const companyValue = next.find((f) => f.key === 'company')?.value ?? '';
    sessionStorageUtils.setUserItem('projectListCompanyFilter', companyValue);
    setActiveFilters(next);
    setCurrentPage(1);
  }, []);

  const statusFilterValue = activeFilters.find((f) => f.key === 'status')?.value;
  const companyFilterText = activeFilters.find((f) => f.key === 'company')?.value ?? '';
  const [debouncedCompanyFilterText, setDebouncedCompanyFilterText] =
    React.useState(companyFilterText);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedCompanyFilterText(companyFilterText), 400);
    return () => clearTimeout(timer);
  }, [companyFilterText]);

  const { companies } = useGetCompaniesQuery();

  const isCompanyFilterActive = debouncedCompanyFilterText.length >= 3;
  const { companies: filteredCompanies } = useGetV1CompaniesQuery(debouncedCompanyFilterText, {
    enabled: isCompanyFilterActive,
  });

  const companyIds = useMemo(() => {
    if (!isCompanyFilterActive) {
      return undefined;
    }
    // While the company lookup is still loading (or returns nothing), treat it as "no match"
    // by falling back to an empty array rather than undefined. Otherwise the projects query
    // would run unfiltered and return all projects.
    return filteredCompanies?.map((c) => c.id) ?? [];
  }, [isCompanyFilterActive, filteredCompanies]);

  // Filter is active but nothing matched (or matches not yet loaded) → skip query and show no results.
  const queryEnabled = companyIds === undefined || companyIds.length > 0;

  const offset = (currentPage - 1) * pageSize;

  const { projects, pagination, isLoading, errorList, refetch } = useProjectsQuery({
    limit: pageSize,
    offset,
    orderBy,
    lifecycleStatus:
      isOrganizationAdmin && statusFilterValue
        ? toLifecycleStatusFilter(statusFilterValue as StatusFilter)
        : undefined,
    name: debouncedSearchText.trim() || undefined,
    companyIds,
    enabled: queryEnabled,
  });

  // Keep the last known pagination so the top pager stays visible while a new page loads.
  const lastPaginationRef = React.useRef(pagination);
  if (pagination) {
    lastPaginationRef.current = pagination;
  }
  const stablePagination = pagination ?? lastPaginationRef.current;

  const companyOptions = useMemo(() => {
    if (!hasAdminRights || !companies) {
      return [];
    }
    if (isOrganizationAdmin) {
      return companies;
    }
    const companyAdminIds = new Set(currentUser?.companyusers ?? []);
    return companies.filter((c) => companyAdminIds.has(c.id));
  }, [hasAdminRights, isOrganizationAdmin, companies, currentUser]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const serverSorting = useMemo<ServerSortingProps>(() => {
    const parsed = parseOrderBy(orderBy);
    return {
      columnKey: parsed?.columnKey,
      order: parsed?.order,
      onSortChange: (columnKey, order) => {
        setOrderBy(toOrderBy(columnKey, order));
        setCurrentPage(1);
      },
    };
  }, [orderBy]);

  const filterDefinitions = useMemo<FilterDefinition[]>(() => {
    const companyFilter: FilterDefinition = {
      key: 'company',
      label: t('unifiedProjectList.companyFilter.label', { ns: 'components' }),
      type: 'text',
      placeholder: t('unifiedProjectList.companyFilter.placeholder', { ns: 'components' }),
    };

    if (!isOrganizationAdmin) {
      return [companyFilter];
    }

    const groupArchive = t('unifiedProjectList.statusFilter.groupArchive', { ns: 'components' });
    const groupRestore = t('unifiedProjectList.statusFilter.groupRestore', { ns: 'components' });
    const statusFilterLabel = t('unifiedProjectList.statusFilter.label', { ns: 'components' });
    return [
      {
        key: 'status',
        label: statusFilterLabel,
        options: [
          {
            value: 'ACTIVE',
            label: t('unifiedProjectList.statusFilter.active', { ns: 'components' }),
          },
          {
            value: 'DISABLED',
            label: t('unifiedProjectList.statusFilter.disabled', { ns: 'components' }),
          },
          {
            value: 'ALL_ARCHIVED',
            label: t('unifiedProjectList.statusFilter.allArchived', { ns: 'components' }),
            group: groupArchive,
          },
          {
            value: 'ARCHIVED',
            label: t('unifiedProjectList.statusFilter.archived', { ns: 'components' }),
            group: groupArchive,
          },
          {
            value: 'ARCHIVE_IDLE',
            label: t('unifiedProjectList.statusFilter.archiveIdle', { ns: 'components' }),
            group: groupArchive,
          },
          {
            value: 'ARCHIVE_IN_PROGRESS',
            label: t('unifiedProjectList.statusFilter.archiveInProgress', { ns: 'components' }),
            group: groupArchive,
          },
          {
            value: 'ARCHIVE_ERROR',
            label: t('unifiedProjectList.statusFilter.archiveError', { ns: 'components' }),
            group: groupArchive,
          },
          {
            value: 'ALL_RESTORED',
            label: t('unifiedProjectList.statusFilter.allRestored', { ns: 'components' }),
            group: groupRestore,
          },
          {
            value: 'RESTORE_IDLE',
            label: t('unifiedProjectList.statusFilter.restoreIdle', { ns: 'components' }),
            group: groupRestore,
          },
          {
            value: 'RESTORE_IN_PROGRESS',
            label: t('unifiedProjectList.statusFilter.restoreInProgress', { ns: 'components' }),
            group: groupRestore,
          },
          {
            value: 'RESTORE_ERROR',
            label: t('unifiedProjectList.statusFilter.restoreError', { ns: 'components' }),
            group: groupRestore,
          },
        ],
      },
      companyFilter,
    ];
  }, [isOrganizationAdmin, t]);

  const projectColumns = useMemo<DataTableColumns<ProjectTableView>>(() => {
    const baseColumns: DataTableColumns<ProjectTableView> = {
      name: {
        sortableBy: { type: DataTableSortType.STRING },
        title: t('unifiedProjectList.columns.name', { ns: 'components' }),
        unfilterable: true,
      },
      company: {
        title: t('unifiedProjectList.columns.company', { ns: 'components' }),
        unfilterable: true,
      },
      created: {
        monospaced: 'montserrat',
        sortableBy: { type: DataTableSortType.DATE },
        formatter: (value: string) => (isDefined(value) ? formattedDate(value) : ''),
        title: t('unifiedProjectList.columns.created', { ns: 'components' }),
        unfilterable: true,
        width: '170px',
      },
    };

    if (!isOrganizationAdmin) {
      return baseColumns;
    }
    return {
      status: {
        alignment: 'left',
        unfilterable: true,
        width: '40px',
      },
      ...baseColumns,
      size: {
        alignment: 'right',
        monospaced: 'montserrat',
        title: t('unifiedProjectList.columns.size', { ns: 'components' }),
        formatter: (value: number) => toHumanReadableFileSize(value),
        unfilterable: true,
        width: '125px',
      },
    };
  }, [isOrganizationAdmin, t]);

  const projectsTableView = useMemo<ProjectTableView[]>(() => {
    if (!isDefined(projects)) {
      return [];
    }

    return projects.map((project) => ({
      status: <>{ProjectStatusIcon(resolveExtendedStatus(project), theme, t)}</>,
      id: project.id,
      name: project.name,
      company: project.company?.name ?? '',
      companyId: project.company?.id,
      created: project.createdAt,
      size: project.size,
    }));
  }, [projects, theme, t]);

  const { isCompanyLicenseValid, showExpiredDialog, renderExpiredLicenseDialog } =
    useExpiredLicenseGuard();

  const onRowClicked = useCallback(
    (project: ProjectTableView) => {
      if (!isOrganizationAdmin && !isCompanyLicenseValid(project.companyId)) {
        showExpiredDialog(project.companyId!, project.id);
        return;
      }

      void navigate(`/projects/${project.id}`, { state: { companyId: project.companyId } });
    },
    [isOrganizationAdmin, isCompanyLicenseValid, navigate, showExpiredDialog],
  );

  if (errorList) {
    return errorList;
  }

  const numberOfPages = isDefined(stablePagination)
    ? getNumberOfPages(stablePagination.total, pageSize)
    : 0;
  const firstItem = (currentPage - 1) * pageSize + 1;
  const lastItem = isDefined(stablePagination)
    ? Math.min(currentPage * pageSize, stablePagination.total)
    : 0;

  return (
    <>
      {renderExpiredLicenseDialog()}
      <ControlRow>
        <SearchGroup>
          <TextBox
            autoFocus={true}
            placeholder={t('search', { ns: 'common' })}
            value={searchText}
            width={250}
            onChange={(e) => {
              setSearchText(e.target.value);
              setCurrentPage(1);
            }}
          />
          <TableFilterButton
            activeFilters={activeFilters}
            definitions={filterDefinitions}
            onChange={handleFiltersChange}
          />
        </SearchGroup>
        <RightControls direction="row">
          {hasAdminRights && (
            <CreateProjectButton companies={companyOptions} onCreated={() => void refetch()} />
          )}
          {isDefined(stablePagination) && (
            <RangeNav>
              <RangeText className="notranslate">
                {firstItem}–{lastItem} {t('unifiedProjectList.pagination.of', { ns: 'components' })}{' '}
                {stablePagination.total}
              </RangeText>
              <NavButton
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                <Icon fixedWidth={true} icon={['fas', 'chevron-left']} size="lg" />
              </NavButton>
              <NavButton
                disabled={currentPage >= numberOfPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                <Icon fixedWidth={true} icon={['fas', 'chevron-right']} size="lg" />
              </NavButton>
            </RangeNav>
          )}
        </RightControls>
      </ControlRow>

      <TableFilterChips
        activeFilters={activeFilters}
        definitions={filterDefinitions}
        onChange={handleFiltersChange}
      />

      <DataTable
        columns={projectColumns}
        fixedLayout={true}
        items={projectsTableView}
        loading={isLoading}
        pageSize={pageSize}
        serverSorting={serverSorting}
        tableStyle={tableStyle}
        onRowClicked={onRowClicked}
      />
    </>
  );
};

const SearchGroup = styled.div`
  display: inline-flex;
  align-items: stretch;
`;

const ControlRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
`;

const RightControls = styled(Stack)`
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
`;

const RangeNav = styled.div`
  display: flex;
  align-items: center;
  gap: 0.125rem;
`;

const RangeText = styled.span`
  font-size: 13px;
  white-space: nowrap;
  padding: 0 0.25rem;
  min-width: 8.5em;
  text-align: right;
`;

const NavButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 3px 5px;
  color: inherit;
  line-height: 1;
  border-radius: 3px;

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.06);
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }
`;

export { UnifiedProjectList };
